import { useCallback, useEffect, useReducer, useState } from "react";
import { CSSTransition } from "react-transition-group";
import { Button, Search, Select, Table } from "semantic-ui-react";
import { countryOptions, lortOptions, voltageOptions } from "../../../../../../models/nec";
import { Loading } from "../../../../../Loading/Loading";
import { extractLinesCGM } from "../../../zipLinesHelper";
import { CriticalBranchesForm } from "../CriticalBranchesForm/CriticalBranchesForm";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../../../store/configureStore";
import { IBranch } from "../../../../../../store/cgmBranchesReducer";
import {
    AddCGMCriticalBranch,
    AddCGMOutage,
    RemoveCriticalBranchCGM,
    RemoveOutageCGM,
    ResetCGMCriticalBranch,
    SetCGMCriticalBranchBranchInput,
    SetCGMOutageBranchInput,
    SetCGMCountry,
    SetCGMLort,
    SetCGMVoltage,
    ToggleCGMFormOpen,
    SetCGMOutageBranchEic,
} from "../../../../../../actions/cgmBranchesActions";
import { sortReducer } from "../sortReducer";
import { toast } from "../../../../../..";

export const NECSelectorCGM = () => {
    const dispatch = useDispatch();
    const zipFile = useSelector((state: AppState) => state.files.zipFile);
    const activeTimestamps = useSelector((state: AppState) => state.timestamps.timestamps);
    const { cgmBranch, formOpen, country, lort, voltage } = useSelector(
        (state: AppState) => state.cgmBranches
    );
    const [loading, setLoading] = useState(false);
    const [lines, setLines] = useState<IBranch[]>([]);
    const [searchString, setSearchString] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [state, dispatchLocal] = useReducer(sortReducer, {
        column: null,
        data: lines,
        direction: null,
    });
    const { column, data, direction } = state;
    const [selectedLine, setSelectedLine] = useState<IBranch>();
    const [selectedRow, setSelectedRow] = useState("");

    const searchCGM = (e: any, data: any) => {
        if (data.value.length >= 3) setSearchString(data.value);
        else if (data.value.length === 0) setSearchString("");
        setSearchValue(data.value);
    };

    const handleSelectLine = (line: IBranch, key: string) => {
        setSelectedLine({ ...line, key: key });
        setSelectedRow(key);
    };

    const handleAddBranch = (type: string) => {
        if (!formOpen) dispatch(ToggleCGMFormOpen(true));
        if (selectedLine === undefined) toast("Please select a branch", false);
        else {
            if (type === "cb") {
                if (cgmBranch.criticalBranch) toast("Critical branch already selected", false);
                else {
                    dispatch(AddCGMCriticalBranch(selectedLine));
                    toast("Critical branch added", true);
                    setSelectedRow("");
                }
            } else {
                if (cgmBranch.outage!.branches.includes(selectedLine))
                    toast("Outage already selected", false);
                else if (selectedLine === cgmBranch.criticalBranch)
                    toast("Branch already selected as critical branch", false);
                else {
                    dispatch(AddCGMOutage(selectedLine));
                    toast("Critical outage added", true);
                    setSelectedRow("");
                }
            }
        }
    };

    const handleCloseForm = () => {
        dispatch(ToggleCGMFormOpen(false));
        dispatch(ResetCGMCriticalBranch());
        setSelectedRow("");
        setTimeout(() => setSelectedLine(undefined), 1000);
    };

    const handleRemoveBranch = (key: string, type: string) => {
        if (type === "cb") {
            dispatch(RemoveCriticalBranchCGM());
            toast("Critical branch removed", true);
        } else {
            dispatch(RemoveOutageCGM(key));
            toast("Critical outage removed", true);
        }
    };

    const handleCriticalBranchBranchInputChange = useCallback(
        (e: any, type: string) => {
            if (type === "cb") dispatch(SetCGMCriticalBranchBranchInput(e));
            else dispatch(SetCGMOutageBranchInput(e));
        },
        [dispatch]
    );

    const handleOutageBranchEic = (key: string, val: string) =>
        dispatch(SetCGMOutageBranchEic(key, val));

    useEffect(() => {
        dispatchLocal({ type: "SET_DATA", data: lines });
    }, [lines]);

    useEffect(() => {
        if (zipFile && country && activeTimestamps.length > 0 && lort) {
            setLoading(true);
            extractLinesCGM(zipFile, country, activeTimestamps, lort, voltage, searchString).then(
                newLines => {
                    setLines(newLines);
                    setLoading(false);
                }
            );
        }
    }, [zipFile, country, lort, voltage, searchString, activeTimestamps]);

    return (
        <div id="div-NECSelectorCGM-a" className="necs-selector-container transition-container">
            <div id="selectors-lvl-1-NECSelectorCGM-a" className="selectors-lvl-1">
                <div id="wrapper-NECSelectorCGM-a" className="necs-selector-wrapper">
                    <p id="p-NECSelectorCGM-country">Country</p>
                    <Select
                        id="select-NECSelectorCGM-a"
                        search
                        selectOnBlur={false}
                        upward={false}
                        className="country-select"
                        options={countryOptions}
                        onChange={(e, data: any) => dispatch(SetCGMCountry(data.value))}
                        value={country}
                    />
                </div>
                <div id="wrapper-NECSelectorCGM-b" className="necs-selector-wrapper">
                    <p id="p-NECSelectorCGM-lines/transformers">Lines/transformers</p>
                    <Select
                        id="select-NECSelectorCGM-b"
                        selectOnBlur={false}
                        upward={false}
                        className="lort-select"
                        options={lortOptions}
                        onChange={(e, data: any) => dispatch(SetCGMLort(data.value))}
                        value={lort}
                    />
                </div>
                <div id="wrapper-NECSelectorCGM-c" className="necs-selector-wrapper">
                    <p id="p-NECSelectorCGM-voltageLevel">Voltage level</p>
                    <Select
                        id="select-NECSelectorCGM-c"
                        clearable
                        selectOnBlur={false}
                        upward={false}
                        className="voltage-select"
                        options={voltageOptions}
                        onChange={(e, data: any) => dispatch(SetCGMVoltage(data.value))}
                        value={voltage}
                    />
                </div>
                <Search
                    id="search-NECSelectorCGM-a"
                    placeholder="Search"
                    showNoResults={false}
                    upward={false}
                    className="cgm-search"
                    onSearchChange={searchCGM}
                    value={searchValue}
                />
            </div>
            {loading ? (
                <Loading />
            ) : country && zipFile && lort && activeTimestamps.length > 0 ? (
                <div id="div-container-NECSelectorCGM-a" className="neselector-container">
                    <Table id ="table-NECSelectorCGM-a" sortable selectable celled className="cgm-table">
                        <Table.Header id="tableHeader-NECSelectorCGM-a">
                            <Table.Row id="tableRow-NECSelectorCGM-a">
                                <Table.HeaderCell
                                    id="tableHeaderCell-NECSelectorCGM-a"
                                    sorted={column === "timestamp" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "timestamp",
                                        })
                                    }
                                >
                                    Timestamp
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    id="tableHeaderCell-NECSelectorCGM-b"
                                    sorted={column === "node1" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "node1",
                                        })
                                    }
                                >
                                    Node 1
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    id="tableHeaderCell-NECSelectorCGM-c"
                                    sorted={column === "node2" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "node2",
                                        })
                                    }
                                >
                                    Node 2
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    id="tableHeaderCell-NECSelectorCGM-d"
                                    sorted={column === "orderCode" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "orderCode",
                                        })
                                    }
                                >
                                    Order
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    id="tableHeaderCell-NECSelectorCGM-e"
                                    sorted={column === "elementName" ? direction : null}
                                    onClick={() =>
                                        dispatchLocal({
                                            type: "CHANGE_SORT",
                                            column: "elementName",
                                        })
                                    }
                                >
                                    Element name
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        {data.length !== 0 &&
                            data.map((l: IBranch) => (
                                <Table.Body
                                    id={`tableBody-NECSelectorCGM-a-${l.elementName}`}
                                    key={`${l.timestamp}${l.node1}${l.node2}${l.orderCode}${l.elementName}`}
                                >
                                    <Table.Row
                                        id={`tableRow-NECSelectorCGM-b-${l.elementName}`}
                                        className={`${
                                            selectedRow ===
                                            `${l.node1}${l.node2}${l.orderCode}${l.elementName}`
                                                ? "selected-row"
                                                : ""
                                        }`}
                                        key={`${l.node1}${l.node2}${l.orderCode}${l.elementName}`}
                                        onClick={() =>
                                            handleSelectLine(
                                                l,
                                                `${l.node1}${l.node2}${l.orderCode}${l.elementName}`
                                            )
                                        }
                                    >
                                        <Table.Cell id={`tableCell-NECSelectorCGM-ac-${l.elementName}`}>{l.timestamp}</Table.Cell>
                                        <Table.Cell id={`tableCell-NECSelectorCGM-bc-${l.elementName}`}>{l.node1}</Table.Cell>
                                        <Table.Cell id={`tableCell-NECSelectorCGM-cc-${l.elementName}`}>{l.node2}</Table.Cell>
                                        <Table.Cell id={`tableCell-NECSelectorCGM-dc-${l.elementName}`}>{l.orderCode}</Table.Cell>
                                        <Table.Cell id={`tableCell-NECSelectorCGM-ec-${l.elementName}`}>{l.elementName}</Table.Cell>
                                    </Table.Row>
                                </Table.Body>
                            ))}
                    </Table>
                    <CSSTransition
                        id="CSSTransition-NECSelectorCGM-c"
                        in={lines.length > 0}
                        timeout={400}
                        classNames="neselector-transition"
                        unmountOnExit
                    >
                        <div id="neselector-actions-message">
                            <Button.Group id="buttonGroup-NECSelectorCGM-a" className="neselector-actions" size="large">
                                <Button
                                    id="button-NECSelectorCGM-b"
                                    primary
                                    content="Add CB"
                                    onClick={() => handleAddBranch("cb")}
                                />
                                <Button.Or id="buttonOr-NECSeelectorCGm" />
                                <Button
                                    id="button-NECSelectorCGM-c"
                                    primary
                                    content="Add CO"
                                    onClick={() => handleAddBranch("co")}
                                />
                            </Button.Group>
                        </div>
                    </CSSTransition>
                    <CSSTransition
                        id="CSSTransition-NECSelectorCGM-d"
                        in={formOpen}
                        timeout={400}
                        classNames="neselector-transition"
                        unmountOnExit
                    >
                        <CriticalBranchesForm
                            handleCloseForm={handleCloseForm}
                            handleRemoveBranch={handleRemoveBranch}
                            handleCriticalBranchBranchInputChange={
                                handleCriticalBranchBranchInputChange
                            }
                            handleOutageBranchEic={handleOutageBranchEic}
                        />
                    </CSSTransition>
                </div>
            ) : !zipFile ? (
                <p id="p-NECSelectorCGM-uploadFile">Please upload a file</p>
            ) : !country ? (
                <p id="p-NECSelectorCGM-selectCountry">Please select country</p>
            ) : !lort ? (
                <p id="p-NECSelectorCGM-selectLinesTransformers">Please select lines or transformers</p>
            ) : activeTimestamps.length === 0 ? (
                <p id="p-NECSelectorCGM-selectTimestamp">Please select at least one timestamp</p>
            ) : null}
        </div>
    );
};
